/**
 * pedagogyConcepts — catalogue des concepts pédagogiques du jeu SCD.
 *
 * 11/02/2026 (Phase 2 — Pédagogie apprenant).
 *
 * Chaque concept est associé à :
 *   - id            : slug unique (utilisé en clé de state)
 *   - label         : nom affiché à l'apprenant
 *   - definition    : phrase courte (2-3 lignes max) — sert de tooltip glossaire
 *                     ET de description dans la carte « concept débloqué »
 *   - category      : Finance | Commerce | RH | Fiscalité | Stratégie | Opérationnel
 *   - checkFn       : (state) => boolean. Retourne true quand la mécanique
 *                     associée a été rencontrée pour la première fois.
 *   - trigger_hint  : phrase pédagogique « Vous venez de … »
 *
 * IMPORTANT : les checkFn doivent être :
 *   - idempotents (aucun side-effect, aucun set)
 *   - rapides (< 1 ms — appelés chaque nextDay)
 *   - défensifs (jamais crasher si un champ est undefined)
 *
 * Concepts couverts (v1) : 12 fondamentaux logistique + gestion.
 */

export const PEDAGOGY_CONCEPTS = [
  {
    id: 'tresorerie',
    label: 'Trésorerie',
    category: 'Finance',
    definition:
      "L'argent liquide immédiatement disponible sur le compte de l'entreprise. Différent du chiffre d'affaires : on peut avoir des ventes mais pas de cash si les clients paient tard.",
    trigger_hint: 'Vous suivez désormais la trésorerie de votre entreprise.',
    checkFn: (s) => (s.totalDays || 0) >= 1 && !!s.gameStarted,
  },
  {
    id: 'contrat_actif',
    label: 'Contrat de transport',
    category: 'Commerce',
    definition:
      "Engagement contractuel avec un client pour transporter des marchandises sur une période donnée. Génère un chiffre d'affaires récurrent mais impose des livraisons régulières sous peine de pénalités.",
    trigger_hint: 'Vous avez signé votre premier contrat client.',
    checkFn: (s) => Array.isArray(s.activeContracts) && s.activeContracts.length >= 1,
  },
  {
    id: 'embauche',
    label: 'Embauche & masse salariale',
    category: 'RH',
    definition:
      "Recruter un chauffeur ajoute son salaire brut chargé (env. 1,42× le brut) à vos charges fixes mensuelles. La masse salariale pèse sur le résultat même quand l'activité baisse.",
    trigger_hint: 'Vous avez embauché votre premier chauffeur.',
    checkFn: (s) => Array.isArray(s.drivers) && s.drivers.length >= 1,
  },
  {
    id: 'amortissement',
    label: 'Amortissement',
    category: 'Finance',
    definition:
      "Étalement comptable de l'usure d'un véhicule sur sa durée de vie (5 à 7 ans). Réduit le résultat imposable chaque année sans sortie de cash — un outil de pilotage fiscal.",
    trigger_hint: 'Vous détenez un véhicule qui va s\'amortir dans le temps.',
    checkFn: (s) => Array.isArray(s.vehicles) && s.vehicles.some((v) => v && !v.leasing && !v.rental),
  },
  {
    id: 'credit',
    label: 'Crédit d\'investissement',
    category: 'Finance',
    definition:
      "Prêt bancaire pour financer un actif (agencement, magasin, matériel). Rembourse en mensualités = capital + intérêts. Améliore la trésorerie court terme mais crée une dette et des charges financières.",
    trigger_hint: 'Vous avez souscrit votre premier crédit.',
    checkFn: (s) => Array.isArray(s.credits) && s.credits.length >= 1,
  },
  {
    id: 'is_impot',
    label: 'Impôt sur les sociétés (IS)',
    category: 'Fiscalité',
    definition:
      "Impôt annuel sur le bénéfice de l'entreprise. En France : 15% jusqu'à 42 500 € de bénéfice, 25% au-delà. Payé en 4 acomptes trimestriels — à provisionner tôt pour éviter la surprise de trésorerie.",
    trigger_hint: 'Vous avez déclenché le calcul de votre premier IS.',
    checkFn: (s) => (s.isProvisioned || 0) > 0 || (s.taxHistory && s.taxHistory.length > 0),
  },
  {
    id: 'tva',
    label: 'TVA collectée / déductible',
    category: 'Fiscalité',
    definition:
      "Vous collectez la TVA (20%) sur vos factures clients et la déduisez sur vos achats. La différence est reversée à l'État chaque mois (ou trimestre). Attention : la TVA n'est pas un revenu — elle transite par vos comptes.",
    trigger_hint: "Vous suivez désormais votre TVA collectée / déductible.",
    checkFn: (s) => (s.tvaCollected || 0) > 0 || (s.tvaHistory && s.tvaHistory.length > 0),
  },
  {
    id: 'notoriete',
    label: 'Notoriété',
    category: 'Stratégie',
    definition:
      "Indicateur de la réputation de votre entreprise (0-100). Influe sur les opportunités commerciales, l'attractivité RH et les conditions bancaires. Se construit lentement, se détruit vite.",
    trigger_hint: 'La notoriété de votre entreprise commence à se construire.',
    checkFn: (s) => (s.reputation || 0) >= 40, // au-delà du niveau initial 35
  },
  {
    id: 'penalite',
    label: 'Pénalité de contrat',
    category: 'Commerce',
    definition:
      "Sanction financière prévue au contrat pour une livraison manquée ou en retard. Souvent 5 à 15% du CA mensuel du contrat, et abîme la relation client (risque de rupture).",
    trigger_hint: 'Vous venez de subir votre première pénalité — leçon apprise.',
    checkFn: (s) => (s.penaltiesPaidTotal || 0) > 0 || (s.contractHistory || []).some((c) => (c.penaltiesPaid || 0) > 0),
  },
  {
    id: 'concurrence',
    label: 'Analyse concurrentielle',
    category: 'Stratégie',
    definition:
      "Étude des concurrents (parts de marché, prix, flotte, réputation) pour ajuster votre stratégie. Se consulte dans l'onglet Concurrence : identifiez les segments où vous êtes vulnérable ou dominant.",
    trigger_hint: 'Vous avez consulté vos concurrents.',
    checkFn: (s) => !!s.competitorPageVisited,
  },
  {
    id: 'appel_offre',
    label: 'Appel d\'offres (AO)',
    category: 'Commerce',
    definition:
      "Contrat lancé par un client important qui met plusieurs transporteurs en concurrence. Volume élevé mais marge serrée : un AO gagné trop bas peut détruire de la valeur.",
    trigger_hint: "Vous avez répondu à votre premier appel d'offres.",
    checkFn: (s) => Array.isArray(s.tenderResponses) && s.tenderResponses.length >= 1,
  },
  {
    id: 'phase_up',
    label: 'Passage de phase',
    category: 'Stratégie',
    definition:
      "Le jeu progresse en 4 phases (Local → Régional → National → International). Chaque phase débloque de nouveaux dépôts, contrats et défis. La transition demande un capital solide et une équipe formée.",
    trigger_hint: 'Vous avez franchi une nouvelle phase stratégique.',
    checkFn: (s) => (s.phase || 1) >= 2,
  },
];

export const CONCEPT_BY_ID = Object.fromEntries(
  PEDAGOGY_CONCEPTS.map((c) => [c.id, c]),
);

/**
 * Groupe les concepts par catégorie — utilisé par le bilan PDF et la page
 * « Concepts découverts ».
 */
export function groupConceptsByCategory(concepts = PEDAGOGY_CONCEPTS) {
  const map = new Map();
  for (const c of concepts) {
    if (!map.has(c.category)) map.set(c.category, []);
    map.get(c.category).push(c);
  }
  return map;
}
